// src/admin/AttendanceLogManager.js - Bổ sung cột Thao tác và nút Xóa lượt điểm danh sai
import React, { useState, useEffect, useMemo } from 'react';
import { firestore } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  deleteField
} from 'firebase/firestore';
import './AttendanceLogManager.css';

const BATCH_SIZE = 50; // Số lượng dòng nhật ký hiển thị mỗi lần (Lazy Load)

const AttendanceLogManager = () => {
  const [activeTab, setActiveTab] = useState('mass'); // 'mass' (Đi Lễ) hoặc 'catechism' (Học Giáo Lý)
  const [filters, setFilters] = useState({ id: '', name: '', class: '', catechist: '', time: '', action: '' });

  // State Dữ liệu Firestore
  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [massLogs, setMassLogs] = useState([]);
  const [catechismLogs, setCatechismLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);

  // State Phân trang / Lazy Load
  const [displayLimit, setDisplayLimit] = useState(BATCH_SIZE);

  // Hàm định dạng ISO time_log sang dd/mm/yyyy hh:mm:ss
  const formatTime = (timeLog) => {
    if (!timeLog) return '';
    try {
      const date = new Date(timeLog);
      if (isNaN(date.getTime())) return String(timeLog);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      return String(timeLog);
    }
  };

  // ====================================================================
  // TẢI DỮ LIỆU ĐIỂM DANH TỪ FIRESTORE
  // ====================================================================
  useEffect(() => {
    const fetchAttendanceLogsFromFirestore = async () => {
      setLoading(true);
      try {
        // 1. Lấy Niên khóa Hiện tại (isCurrent == true)
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) currentYearDoc = allYearsSnap.docs[0];
        }

        if (!currentYearDoc) {
          setLoading(false);
          return;
        }

        const yearId = currentYearDoc.id;
        setCurrentYearId(yearId);
        setCurrentYearName(currentYearDoc.data().name || yearId);

        // 2. Tải song song danh sách học sinh, lớp, phân lớp và điểm danh
        const [studentsSnap, classesSnap, enrollmentsSnap, studentAttendancesSnap] = await Promise.all([
          getDocs(collection(firestore, 'students')),
          getDocs(collection(firestore, `academic_years/${yearId}/classes`)),
          getDocs(collection(firestore, `academic_years/${yearId}/enrollments`)),
          getDocs(collection(firestore, `academic_years/${yearId}/student_attendances`))
        ]);

        const studentsMap = {};
        studentsSnap.docs.forEach(d => {
          studentsMap[d.id] = d.data();
        });

        const classesMap = {};
        classesSnap.docs.forEach(d => {
          classesMap[d.id] = d.data().name || d.id;
        });

        const studentClassMap = {};
        enrollmentsSnap.docs.forEach(d => {
          const enr = d.data();
          const sId = enr.studentId || d.id;
          studentClassMap[sId] = classesMap[enr.classId] || enr.classId || 'Chưa xếp lớp';
        });

        const fetchedMassLogs = [];
        const fetchedCatechismLogs = [];

        studentAttendancesSnap.docs.forEach(docSnap => {
          const studentId = docSnap.id;
          const docData = docSnap.data();

          const studentProfile = studentsMap[studentId] || {};
          const className = studentClassMap[studentId] || 'Chưa xếp lớp';

          const churchMap = docData.church || {};
          const catechismMap = docData.catechism || {};

          // (A) Nhật ký Đi Lễ Chúa Nhật
          Object.keys(churchMap).forEach(dateStr => {
            const att = churchMap[dateStr];
            if (att && typeof att === 'object') {
              fetchedMassLogs.push({
                studentId: studentId,
                holyName: studentProfile.holyName || '',
                fullName: studentProfile.fullName || studentId,
                className: className,
                catechist: att.catechist || 'Tự động',
                timeLog: att.time_log || '',
                formattedTime: formatTime(att.time_log),
                date: dateStr,
                action: att.time || 'Đã quét',
                type: 'church'
              });
            }
          });

          // (B) Nhật ký Học Giáo Lý
          Object.keys(catechismMap).forEach(dateStr => {
            const att = catechismMap[dateStr];
            if (att && typeof att === 'object') {
              fetchedCatechismLogs.push({
                studentId: studentId,
                holyName: studentProfile.holyName || '',
                fullName: studentProfile.fullName || studentId,
                className: className,
                catechist: att.catechist || 'Tự động',
                timeLog: att.time_log || '',
                formattedTime: formatTime(att.time_log),
                date: dateStr,
                action: att.time || 'Đã quét',
                type: 'catechism'
              });
            }
          });
        });

        const sortLogs = (list) => list.sort((a, b) => {
          if (a.timeLog && b.timeLog) {
            return new Date(b.timeLog) - new Date(a.timeLog);
          }
          return b.date.localeCompare(a.date);
        });

        setMassLogs(sortLogs(fetchedMassLogs));
        setCatechismLogs(sortLogs(fetchedCatechismLogs));

      } catch (err) {
        console.error('Lỗi khi tải nhật ký điểm danh từ Firestore:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceLogsFromFirestore();
  }, []);

  // Reset giới hạn Lazy Load khi chuyển Tab
  useEffect(() => {
    setDisplayLimit(BATCH_SIZE);
  }, [activeTab]);

  const currentLogs = activeTab === 'mass' ? massLogs : catechismLogs;

  // Lọc nhật ký theo điều kiện tìm kiếm
  const filteredLogs = useMemo(() => {
    return currentLogs.filter((log) => {
      const name = `${log.holyName} ${log.fullName}`.toLowerCase();
      return (
        log.studentId.toLowerCase().includes(filters.id.toLowerCase().trim()) &&
        name.includes(filters.name.toLowerCase().trim()) &&
        log.className.toLowerCase().includes(filters.class.toLowerCase().trim()) &&
        log.catechist.toLowerCase().includes(filters.catechist.toLowerCase().trim()) &&
        (log.formattedTime.toLowerCase().includes(filters.time.toLowerCase().trim()) || log.date.includes(filters.time.trim())) &&
        log.action.toLowerCase().includes(filters.action.toLowerCase().trim())
      );
    });
  }, [currentLogs, filters]);

  const visibleLogs = useMemo(() => {
    return filteredLogs.slice(0, displayLimit);
  }, [filteredLogs, displayLimit]);

  const handleFilterChange = (e, field) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
    setDisplayLimit(BATCH_SIZE);
  };

  const handleLoadMore = () => {
    setDisplayLimit((prev) => prev + BATCH_SIZE);
  };

  // ====================================================================
  // HÀM XÓA LƯỢT ĐIỂM DANH SAI
  // ====================================================================
  const handleDeleteLog = async (log) => {
    if (!currentYearId || !log.studentId || !log.date) return;

    const activityLabel = log.type === 'church' ? 'Đi Lễ' : 'Học Giáo Lý';
    const confirmMessage = `Xác nhận XÓA lượt điểm danh [${activityLabel}]?\n\n• Thiếu nhi: ${log.holyName} ${log.fullName} (${log.studentId})\n• Lớp: ${log.className}\n• Ngày: ${log.date}\n• Giờ: ${log.formattedTime || log.action}`;

    if (!window.confirm(confirmMessage)) return;

    const uniqueKey = `${log.studentId}-${log.date}-${log.type}`;
    setDeletingKey(uniqueKey);

    try {
      const docRef = doc(firestore, `academic_years/${currentYearId}/student_attendances`, log.studentId);
      const fieldPath = `${log.type}.${log.date}`;

      // Sử dụng deleteField() để gỡ bỏ trường ngày cụ thể khỏi map
      await updateDoc(docRef, {
        [fieldPath]: deleteField()
      });

      // Cập nhật lại state cục bộ ngay lập tức
      if (log.type === 'church') {
        setMassLogs((prev) => prev.filter((item) => !(item.studentId === log.studentId && item.date === log.date)));
      } else {
        setCatechismLogs((prev) => prev.filter((item) => !(item.studentId === log.studentId && item.date === log.date)));
      }

      alert('🎉 Đã xóa lượt điểm danh thành công!');
    } catch (err) {
      console.error('Lỗi khi xóa lượt điểm danh:', err);
      alert('❌ Lỗi khi xóa lượt điểm danh: ' + err.message);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="attendance-log-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '1rem' }}>
        <h2 className="log-title" style={{ margin: 0 }}>Quản lý Quy Trình Điểm Danh</h2>
        {currentYearName && (
          <span style={{ fontSize: '13px', backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 'bold', padding: '6px 12px', borderRadius: '20px', border: '1px solid #bfdbfe' }}>
            ⭐ Niên khóa: {currentYearName}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'mass' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('mass')}
        >
          ⛪ Điểm Danh Đi Lễ ({massLogs.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'catechism' ? 'active-tab' : ''}`}
          onClick={() => setActiveTab('catechism')}
        >
          📖 Điểm Danh Học Giáo Lý ({catechismLogs.length})
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="log-table">
          <thead>
            <tr className="table-header-row">
              <th className="header-cell">ID</th>
              <th className="header-cell">Họ và Tên</th>
              <th className="header-cell">Lớp</th>
              <th className="header-cell">GLV Quét Mã</th>
              <th className="header-cell">Thời Gian Quét</th>
              <th className="header-cell">Trạng Thái</th>
              <th className="header-cell header-cell-center">Thao Tác</th>
            </tr>
            {/* Filter Row */}
            <tr className="filter-row">
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm ID"
                  value={filters.id}
                  onChange={(e) => handleFilterChange(e, 'id')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm Tên"
                  value={filters.name}
                  onChange={(e) => handleFilterChange(e, 'name')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm Lớp"
                  value={filters.class}
                  onChange={(e) => handleFilterChange(e, 'class')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm GLV"
                  value={filters.catechist}
                  onChange={(e) => handleFilterChange(e, 'catechist')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm Thời Gian"
                  value={filters.time}
                  onChange={(e) => handleFilterChange(e, 'time')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell">
                <input
                  type="text"
                  placeholder="Tìm trạng thái"
                  value={filters.action}
                  onChange={(e) => handleFilterChange(e, 'action')}
                  className="filter-input"
                />
              </td>
              <td className="filter-cell"></td>
            </tr>
          </thead>
          <tbody className="table-body">
            {loading ? (
              <tr className="empty-row">
                <td colSpan="7" className="empty-message">
                  ⏳ Đang tải dữ liệu điểm danh...
                </td>
              </tr>
            ) : visibleLogs.map((log, index) => {
              const rowKey = `${log.studentId}-${log.date}-${log.type}`;
              const isDeleting = deletingKey === rowKey;

              return (
                <tr key={`${rowKey}-${index}`} className="log-row">
                  <td className="log-cell" style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#2563eb' }}>
                    {log.studentId}
                  </td>
                  <td className="log-cell">
                    <strong>{log.holyName}</strong> {log.fullName}
                  </td>
                  <td className="log-cell">{log.className}</td>
                  <td className="log-cell">{log.catechist}</td>
                  <td className="log-cell" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {log.formattedTime || log.date}
                  </td>
                  <td className="log-cell">
                    <span className="log-action-tag">
                      {log.action}
                    </span>
                  </td>
                  <td className="log-cell log-cell-center">
                    <button
                      onClick={() => handleDeleteLog(log)}
                      disabled={isDeleting}
                      className="log-delete-btn"
                      title="Xóa lượt điểm danh bị sai này"
                    >
                      {isDeleting ? '⏳ Đang xóa...' : '🗑️ Xóa'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && filteredLogs.length === 0 && (
              <tr className="empty-row">
                <td colSpan="7" className="empty-message">
                  Không tìm thấy dữ liệu điểm danh nào phù hợp
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Thanh hiển thị Tiến trình Lazy Load & Nút Tải Thêm */}
      {!loading && filteredLogs.length > 0 && (
        <div className="log-footer-bar">
          <span className="log-info-text">
            Đang hiển thị <strong>{Math.min(displayLimit, filteredLogs.length)}</strong> trên tổng số <strong>{filteredLogs.length}</strong> nhật ký
          </span>
          {displayLimit < filteredLogs.length && (
            <button onClick={handleLoadMore} className="load-more-btn">
              👇 Tải thêm {Math.min(BATCH_SIZE, filteredLogs.length - displayLimit)} nhật ký
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceLogManager;